import { useRef, useState } from "react";
import { Play, Star } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import logo from "@/assets/logo.png";

const TESTIMONIALS = [
  { name: "Client Name", company: "Company Name", quote: "Add a real client quote here highlighting their experience working with Ferguson's." },
  { name: "Client Name", company: "Company Name", quote: "Add a real client quote here highlighting their experience working with Ferguson's." },
  { name: "Client Name", company: "Company Name", quote: "Add a real client quote here highlighting their experience working with Ferguson's." },
];

const StarRow = ({ count = 5 }) => (
  <div className="flex items-center gap-1" aria-label={`${count} out of 5 stars`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        size={15}
        className={i < count ? "fill-gold text-gold" : "text-ink/15"}
      />
    ))}
  </div>
);

const TestimonialCard = ({ item, index }) => (
  <Reveal delay={0.1 * index}>
    <div
      data-testid={`testimonial-card-${index}`}
      className="relative h-full rounded-2xl border border-dashed border-gold/35 bg-white p-6 shadow-sm"
    >
      <span className="absolute -top-2.5 right-5 bg-gold/15 text-gold-muted text-[10px] font-semibold uppercase tracking-wide px-2.5 py-0.5 rounded-full">
        Placeholder
      </span>
      <StarRow count={5} />
      <p className="mt-4 text-sm sm:text-base text-ink-soft italic leading-relaxed">
        "{item.quote}"
      </p>
      <div className="mt-5 pt-4 border-t border-ink/10">
        <p className="font-display font-semibold text-ink" data-testid={`testimonial-name-${index}`}>
          {item.name}
        </p>
        <p className="text-sm text-ink-faint" data-testid={`testimonial-company-${index}`}>
          {item.company}
        </p>
      </div>
    </div>
  </Reveal>
);

export const Testimonials = () => {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);

  const handlePlay = () => {
    setPlaying(true);
    videoRef.current?.play().catch(() => {
      // Playback couldn't start (codec/network edge case) — native controls stay
      // visible so the visitor can retry manually.
    });
  };

  return (
    <section className="py-20 lg:py-28 bg-cream-alt border-y border-ink/10" data-testid="testimonials-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-10">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto">
            <p className="label-eyebrow text-gold-muted mb-3">Client Voices</p>
            <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-ink">
              Trusted by Businesses Across the Caribbean and Beyond.
            </h2>
            <p className="mt-4 text-base sm:text-lg text-ink-soft">
              30 years of cleared cargo, on-time deliveries, and zero compliance failures.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 max-w-3xl mx-auto w-full">
            <div
              className="relative w-full aspect-video rounded-2xl overflow-hidden shadow-xl border border-gold/20 bg-deep"
              data-testid="testimonial-video-container"
            >
              <video
                ref={videoRef}
                src="/media/client-testimonial.mp4"
                controls={playing}
                playsInline
                className="w-full h-full object-cover bg-black"
                data-testid="testimonial-video-player"
              />
              {!playing && (
                <button
                  type="button"
                  onClick={handlePlay}
                  aria-label="Play client testimonial video"
                  data-testid="testimonial-video-thumbnail"
                  className="group absolute inset-0 w-full h-full flex flex-col items-center justify-center gap-4 bg-gradient-to-br from-deep via-deep-surface to-deep"
                >
                  <img src={logo} alt="Ferguson's" className="h-14 sm:h-16 w-auto" />
                  <span className="font-display text-lg sm:text-xl font-semibold text-gold-light">
                    What Our Clients Say
                  </span>
                  <span className="h-16 w-16 rounded-full bg-gold flex items-center justify-center shadow-lg transition-transform group-hover:scale-110">
                    <Play size={26} className="text-[#0A140F] fill-[#0A140F] ml-0.5" />
                  </span>
                </button>
              )}
            </div>
          </div>
        </Reveal>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {TESTIMONIALS.map((item, i) => (
            <TestimonialCard key={i} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
